<template>
  <Transition name="app-notification" @after-leave="$emit('after-leave')">
    <div
      v-if="visible && notification"
      class="app-notification"
      :class="{'app-notification--link': notification.url}"
      role="status"
      @click="activate"
      @touchstart.passive="touchStart"
      @touchend.passive="touchEnd"
    >
      <span class="app-notification__icon" aria-hidden="true">
        <img v-if="notification.iconUrl" :src="notification.iconUrl" alt="">
        <i v-else-if="notification.type === 'error'" class="fas fa-triangle-exclamation"></i>
        <i v-else class="fas fa-bell"></i>
      </span>
      <span class="app-notification__message">{{ notification.message }}</span>
    </div>
  </Transition>
</template>

<script setup>
import {onBeforeUnmount, watch} from 'vue';

const props = defineProps({
  notification: {type: Object, default: null},
  visible: {type: Boolean, required: true},
});
const emit = defineEmits(['close', 'after-leave', 'activate']);
let timer = null;
let startY = null;

const clearTimer = () => {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
};
watch(() => props.visible, (visible) => {
  clearTimer();
  if (visible) {
    timer = window.setTimeout(() => emit('close'), 5000);
  }
}, {immediate: true});

const activate = () => {
  if (props.notification?.url) {
    emit('activate', props.notification);
  }
};
const touchStart = (event) => {
  startY = event.changedTouches[0]?.clientY ?? null;
};
const touchEnd = (event) => {
  const endY = event.changedTouches[0]?.clientY ?? null;
  if (startY !== null && endY !== null && startY - endY >= 30) {
    emit('close');
  }
  startY = null;
};

onBeforeUnmount(clearTimer);
</script>

<style scoped>
.app-notification {
  position: fixed;
  z-index: 10000;
  top: 8px;
  left: 50%;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  box-sizing: border-box;
  width: min(calc(100% - 16px), 560px);
  min-height: 58px;
  padding: 9px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  transform: translateX(-50%);
}

.app-notification--link {
  cursor: pointer;
}
.app-notification__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
  color: #6b6060;
  background: #f0eded;
  font-size: 18px;
}
.app-notification__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.app-notification__message {
  display: -webkit-box;
  overflow: hidden;
  color: #29292e;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.app-notification-enter-active,
.app-notification-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.app-notification-enter-from,
.app-notification-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-100% - 12px));
}
@media (prefers-reduced-motion: reduce) {
  .app-notification-enter-active,
  .app-notification-leave-active {
    transition: none;
  }
}
</style>
